import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, Renderer2, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { AppTopbar } from './app.topbar';
import { AppSidebar } from './app.sidebar';
import { AppFooter } from './app.footer';
import { LayoutService } from '../service/layout.service';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuItem } from 'primeng/api';
import { CirclesAnimation } from '../animations/circles-animation/circles-animation';
import { GeometricAnimation } from '../animations/geometric-animation/geometric-animation';
import { PolygonsAnimation } from '../animations/polygons-animation/polygons-animation';
import { StarsAnimation } from '../animations/stars-animation/stars-animation';

/** Shell raiz do layout: topbar, sidebar (condicional), breadcrumb, animações de fundo e outlet. */
@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [CommonModule, AppTopbar, AppSidebar, RouterModule, AppFooter, BreadcrumbModule, CirclesAnimation, GeometricAnimation, PolygonsAnimation, StarsAnimation],
    template: `
        <div class="layout-wrapper" [ngClass]="containerClass">
            @if (layoutService.layoutConfig().animation) {
                @switch (layoutService.layoutConfig().animationType) {
                    @case ('circles') {
                        <app-circles-animation></app-circles-animation>
                    }
                    @case ('geometric') {
                        <app-geometric-animation></app-geometric-animation>
                    }
                    @case ('polygons') {
                        <app-polygons-animation></app-polygons-animation>
                    }
                    @case ('stars') {
                        <app-stars-animation></app-stars-animation>
                    }
                }
            }

            <app-topbar></app-topbar>
            @if (!isRootRoute()) {
                <app-sidebar></app-sidebar>
            }
            <!--        <app-helpdesk></app-helpdesk>-->
            <!-- Conteúdo principal -->
            <div class="layout-main-container">
                <div class="layout-main">
                    @if (!isRootRoute()) {
                        <p-breadcrumb [model]="items()" [home]="home"></p-breadcrumb>
                    }
                    <router-outlet></router-outlet>
                </div>
                <!-- Footer separado -->
                <div class="layout-footer-container">
                    <app-footer></app-footer>
                </div>
            </div>
            <div class="layout-mask animate-fadein"></div>
        </div>
    `,
    styles: `
        :host ::ng-deep {
            .p-breadcrumb {
                background: transparent !important;
                border: none !important;
                padding: 0 !important;
                padding-bottom: 10px !important;

                .pi,
                .p-breadcrumb-item-label,
                .p-menuitem-text {
                    color: var(--primary-color) !important;
                }
            }

            &.layout-static-inactive {
                .layout-main-container {
                    margin-left: 0;
                }
            }

            /* Tablet e Desktop Mini Sidebar */
            &.layout-sidebar-mini {
                .layout-sidebar {
                    width: 5rem;
                    overflow-x: hidden;
                    transition: width 0.4s cubic-bezier(0.05, 0.74, 0.2, 0.99);

                    .layout-menuitem-root-text {
                        display: none;
                    }

                    .layout-menu-item {
                        .layout-menuitem-text,
                        .layout-submenu-toggler {
                            opacity: 0;
                            transition: opacity 0.3s;
                        }

                        ul {
                            display: none;
                        }
                    }

                    &:hover {
                        width: 21rem;
                        .layout-menuitem-root-text {
                            display: block;
                        }
                        .layout-menuitem-text,
                        .layout-submenu-toggler {
                            opacity: 1;
                        }
                        ul {
                            display: block;
                        }
                    }
                }

                .layout-main-container {
                    margin-left: 5rem;
                }
            }

            &.layout-tablet {
                .layout-main-container {
                    margin-left: 0;
                    padding-left: 1rem;
                }
            }
        }

        .layout-main-container {
            display: flex;
            flex-direction: column;
            min-height: 100vh;
            justify-content: space-between;
            padding: 7.5rem 2rem 0 2rem;
            transition: margin-left var(--layout-section-transition-duration);
            margin-left: 21.5rem;
        }

        .layout-main {
            flex: 1 1 auto;
            width: 100%;
            border-radius: 10px;
            overflow: hidden;
            padding-bottom: 0 !important;
        }

        .layout-footer-container {
            width: 100%;
            padding-top: 1rem;
        }

        /* Mobile (<=768px): a sidebar vira overlay; o conteúdo ocupa toda a largura e NÃO reserva
           o espaço da sidebar. Sem esta regra o margin-left de 21.5rem permanece e corta o conteúdo. */
        @media screen and (max-width: 768px) {
            .layout-main-container {
                margin-left: 0;
                padding-left: 1rem;
                padding-right: 1rem;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppLayout implements OnInit, OnDestroy {
    readonly items = signal<MenuItem[]>([]);
    /** `title` dá nome acessível ao item: só com o ícone o link é anunciado como "link" e nada mais. */
    home: MenuItem = { icon: 'pi pi-home', routerLink: '/', title: 'Início' };

    private menuOutsideClickListener: (() => void) | null = null;

    isRootRoute = signal(false);

    readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    readonly layoutService = inject(LayoutService);
    readonly renderer = inject(Renderer2);
    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        this.layoutService.overlayOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    if (this.isOutsideClicked(event)) {
                        this.hideMenu();
                    }
                });
            }

            if (this.layoutService.layoutState().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((event: NavigationEnd) => {
                this.hideMenu();

                this.isRootRoute.set(event.urlAfterRedirects.split('?')[0] === '/' || event.urlAfterRedirects.split('?')[0] === '');
            });
    }

    isOutsideClicked(event: MouseEvent) {
        const sidebarEl = document.getElementById('app-main-sidebar');
        const topbarMenuButton = document.getElementById('app-main-sidebar-button');
        const eventTarget: Node = event.target as Node;
        return !sidebarEl?.isEqualNode(eventTarget) && !sidebarEl?.contains(eventTarget) && !topbarMenuButton?.isEqualNode(eventTarget) && !topbarMenuButton?.contains(eventTarget);
    }

    hideMenu() {
        this.layoutService.layoutState.update((prev) => ({ ...prev, overlayMenuActive: false, staticMenuMobileActive: false, menuHoverActive: false }));
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
        this.unblockBodyScroll();
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    get containerClass() {
        const state = this.layoutService.layoutState();
        return {
            'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
            'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
            'layout-static-inactive': (state.staticMenuDesktopInactive || this.isRootRoute()) && this.layoutService.layoutConfig().menuMode === 'static',
            'layout-overlay-active': state.overlayMenuActive,
            'layout-mobile-active': state.staticMenuMobileActive,
            'layout-tablet': state.isTablet,
            'layout-sidebar-mini': state.isDesktop && state.staticMenuDesktopInactive
        };
    }

    ngOnDestroy() {
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }

    ngOnInit() {
        // Inicializa o estado da rota raiz
        this.isRootRoute.set(this.router.url.split('?')[0] === '/' || this.router.url.split('?')[0] === '');

        // Atualiza os breadcrumbs inicialmente
        this.items.set(this.createBreadcrumbs(this.activatedRoute.root));

        // Atualiza os breadcrumbs quando a rota mudar
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.items.set(this.createBreadcrumbs(this.activatedRoute.root));
            });
    }

    private createBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: MenuItem[] = []): MenuItem[] {
        const children: ActivatedRoute[] = route.children;
        if (children.length === 0) {
            return breadcrumbs;
        }

        for (const child of children) {
            if (child.snapshot.url.length > 0) {
                const routeURL: string = child.snapshot.url.map((segment) => segment.path).join('/');
                url += `/${routeURL}`;

                const label = child.snapshot.data['breadcrumb'] || routeURL;
                breadcrumbs.push({ label, routerLink: url });
            }
            return this.createBreadcrumbs(child, url, breadcrumbs);
        }

        return breadcrumbs;
    }
}
